// src/pages/Committees/PresidentList.js
import React, { useEffect, useState } from 'react';
import {
    Table, Button, Container, Alert, Pagination, Modal, Form,
    Badge, Row, Col, Card, Spinner, InputGroup, Image
} from 'react-bootstrap';
import {
    FaEdit, FaTrash, FaPlus, FaSearch, FaFilter, FaUser,
    FaCalendar, FaUniversity, FaPhone, FaEnvelope, FaImage, FaCrown
} from 'react-icons/fa';
import { presidentAPI, universityAPI } from '../../api/wbuoaApi';

const PresidentList = () => {
    // State management
    const [presidents, setPresidents] = useState([]);
    const [filteredPresidents, setFilteredPresidents] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentPresident, setCurrentPresident] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        designation: '',
        university: '',
        email: '',
        mobile: '',
        message: '',
        fromdate: '',
        todate: '',
        still_working: '0'
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUniversity, setSelectedUniversity] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const [loadingUniversities, setLoadingUniversities] = useState(false);

    const itemsPerPage = 10;

    // Form label style
    const formLabelStyle = {
        color: '#1a4a2f',
        fontWeight: '500'
    };

    // Fetch presidents and universities when component mounts
    useEffect(() => {
        fetchPresidents();
        fetchUniversities();
    }, []);

    // Apply filters when presidents or filter criteria changes
    useEffect(() => {
        applyFilters();
    }, [presidents, searchTerm, selectedUniversity, selectedStatus, currentPage, sortConfig]);

    const fetchPresidents = async () => {
        setIsLoading(true);
        try {
            const response = await presidentAPI.listPresidents();
            // API returns array directly or wrapped in data property
            const presidentsData = Array.isArray(response) ? response : (response.data || []);
            setPresidents(presidentsData);
            setError(null);
        } catch (err) {
            setError('Failed to fetch presidents: ' + (err.message || 'Server error'));
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUniversities = async () => {
        setLoadingUniversities(true);
        try {
            const response = await universityAPI.listUniversitiesAll();

            // Extract universities from response
            let universitiesData = [];

            if (Array.isArray(response)) {
                universitiesData = response;
            } else if (response?.data && Array.isArray(response.data)) {
                universitiesData = response.data;
            } else if (response?.data?.data && Array.isArray(response.data.data)) {
                universitiesData = response.data.data;
            }

            // Extract university names/abbreviations
            const formattedUniversities = universitiesData
                .map(uni => {
                    return uni.University || uni.university || uni.name || uni.abbreviation || '';
                })
                .filter(name => name && name.trim() !== '')
                .sort((a, b) => a.localeCompare(b));

            setUniversities(formattedUniversities);
        } catch (err) {
            console.error('Failed to fetch universities:', err);
            setError('Failed to load universities list. Using default list.');
            // If API fails, use default universities
            setUniversities(['CU', 'BU', 'JU', 'Kalyani University', 'Burdwan University']);
        } finally {
            setLoadingUniversities(false);
        }
    };

    // Apply filters, sorting and pagination
    const applyFilters = () => {
        let filtered = [...presidents];

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(president =>
                president.name?.toLowerCase().includes(term) ||
                president.email?.toLowerCase().includes(term) ||
                president.university?.toLowerCase().includes(term) ||
                president.designation?.toLowerCase().includes(term) ||
                president.mobile?.toLowerCase().includes(term)
            );
        }

        // Apply university filter
        if (selectedUniversity !== 'all') {
            filtered = filtered.filter(president => president.university === selectedUniversity);
        }

        // Apply status filter
        if (selectedStatus !== 'all') {
            filtered = filtered.filter(president => {
                if (selectedStatus === 'active') return president.still_working === '1' || president.still_working === 1;
                if (selectedStatus === 'past') return president.still_working === '0' || president.still_working === 0;
                return true;
            });
        }

        // Apply sorting
        filtered.sort((a, b) => {
            if (sortConfig.key) {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
            }
            return 0;
        });

        // Calculate pagination
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginated = filtered.slice(startIndex, endIndex);

        setFilteredPresidents(paginated);
        setTotalItems(filtered.length);
        setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    };

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setSelectedUniversity('all');
        setSelectedStatus('all');
        setCurrentPage(1);
        setSortConfig({ key: 'name', direction: 'asc' });
    };

    // Get unique universities from presidents (for filter dropdown)
    const getUniqueUniversitiesFromPresidents = () => {
        return [...new Set(presidents.map(president => president.university))]
            .filter(u => u)
            .sort((a, b) => a.localeCompare(b));
    };

    // Get stats
    const getStats = () => {
        const total = presidents.length;
        const active = presidents.filter(president =>
            president.still_working === '1' || president.still_working === 1
        ).length;
        const past = presidents.filter(president =>
            president.still_working === '0' || president.still_working === 0
        ).length;
        const universitiesCount = [...new Set(presidents.map(president => president.university))].filter(u => u).length;
        const withPhotos = presidents.filter(president =>
            president.prephotopath || president.photoPath || president.presidentphotopath || president.memberphotopath
        ).length;

        return {
            total,
            active,
            past,
            universitiesCount,
            withPhotos
        };
    };

    const stats = getStats();
    const filterUniversities = getUniqueUniversitiesFromPresidents();

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Check if currently active
    const isCurrentlyActive = (todate, still_working) => {
        if (still_working === '1' || still_working === 1) return true;
        if (!todate) return false;
        try {
            const today = new Date();
            const toDate = new Date(todate);
            return toDate > today;
        } catch (e) {
            return false;
        }
    };

    // Build image URL
    const buildImageUrl = (imagePath) => {
        if (!imagePath) return null;

        // If it's already a full URL
        if (imagePath.startsWith('http')) {
            return imagePath;
        }

        // If it starts with /, it's already relative to base
        if (imagePath.startsWith('/')) {
            return `http://localhost:8000${imagePath}`;
        }

        // Otherwise, prepend the base URL
        return `http://localhost:8000/${imagePath}`;
    };

    // Validate form function
    const validateForm = () => {
        const errors = {};
        if (!formData.name?.trim()) errors.name = 'Name is required';
        if (!formData.university?.trim()) errors.university = 'University is required';
        if (!formData.fromdate) errors.fromdate = 'Start date is required';
        if (formData.still_working === '0' && !formData.todate) {
            errors.todate = 'End date is required for past presidents';
        }
        if (formData.todate && formData.fromdate) {
            const fromDate = new Date(formData.fromdate);
            const toDate = new Date(formData.todate);
            if (toDate < fromDate) errors.todate = 'End date cannot be before start date';
        }
        return errors;
    };

    // Handle create new president submission
    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        // Validate photo is selected for new president
        if (!photoFile) {
            setFormErrors({ ...formErrors, photo: 'Photo is required for new president' });
            return;
        }

        setIsCreating(true);
        setError(null);

        try {
            // Create FormData object
            const formDataToSend = new FormData();

            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
                    formDataToSend.append(key, formData[key].toString());
                }
            });

            // IMPORTANT: Append the photo file with the correct field name 'prephotopath'
            // as expected by the backend API
            if (photoFile) {
                formDataToSend.append('prephotopath', photoFile);
            }

            // Debug: Log FormData contents
            for (let pair of formDataToSend.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            // Make API call
            const response = await presidentAPI.createPresident(formDataToSend);

            console.log('Create response:', response);

            // Close modal and reset
            setShowCreateModal(false);
            setCurrentPage(1);

            // Refresh the list
            await fetchPresidents();

            // Show success message
            setSuccess('President created successfully');
            setTimeout(() => setSuccess(null), 3000);

            // Reset form data and photo
            resetForm();

        } catch (err) {
            console.error('Create error:', err);

            // Handle different error formats
            let errorMessage = 'Failed to create president. ';

            if (err.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Error response data:', err.response.data);
                console.error('Error response status:', err.response.status);
                console.error('Error response headers:', err.response.headers);

                errorMessage += err.response.data?.message || err.response.data?.error || `Server error: ${err.response.status}`;
            } else if (err.request) {
                // The request was made but no response was received
                console.error('Error request:', err.request);
                errorMessage += 'No response from server. Please check your connection.';
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error('Error message:', err.message);
                errorMessage += err.message;
            }

            setError(errorMessage);
        } finally {
            setIsCreating(false);
        }
    };

    // Reset form function
    const resetForm = () => {
        setFormData({
            name: '',
            designation: '',
            university: '',
            email: '',
            mobile: '',
            message: '',
            fromdate: '',
            todate: '',
            still_working: '0'
        });
        setPhotoFile(null);
        setPhotoPreview(null);
        setFormErrors({});
    };

    // Handle edit button click
    const handleEdit = (president) => {
        if (!president?.id) {
            setError('Invalid president data');
            return;
        }

        setCurrentPresident(president);
        setFormData({
            name: president.name || '',
            designation: president.designation || '',
            university: president.university || '',
            email: president.email || '',
            mobile: president.mobile || '',
            message: president.message || '',
            fromdate: president.fromdate ? president.fromdate.substring(0, 10) : '',
            todate: president.todate ? president.todate.substring(0, 10) : '',
            still_working: president.still_working?.toString() || '0'
        });

        // Set photo preview if exists
        const imagePath = president.prephotopath || president.photoPath || president.presidentphotopath || president.memberphotopath;
        if (imagePath) {
            const photoUrl = buildImageUrl(imagePath);
            setPhotoPreview(photoUrl);
        } else {
            setPhotoPreview(null);
        }

        setPhotoFile(null);
        setShowEditModal(true);
    };

    // Handle create new president
    const handleCreate = () => {
        setCurrentPresident(null);
        resetForm();
        setShowCreateModal(true);
    };

    // Handle president update
    const handleUpdate = async (e) => {
        e.preventDefault();

        if (!currentPresident?.id) {
            setError('No president selected for update');
            return;
        }

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        setIsUpdating(true);
        setError(null);

        try {
            // Create FormData object for update
            const formDataToSend = new FormData();

            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
                    formDataToSend.append(key, formData[key].toString());
                }
            });

            // IMPORTANT: Append the photo file with the correct field name 'prephotopath'
            // Only append if a new photo is selected
            if (photoFile) {
                formDataToSend.append('prephotopath', photoFile);
            }

            // Debug: Log FormData contents
            for (let pair of formDataToSend.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            // Make API call
            await presidentAPI.updatePresident(currentPresident.id, formDataToSend);

            setShowEditModal(false);
            await fetchPresidents();
            setSuccess('President updated successfully');
            setTimeout(() => setSuccess(null), 3000);

            // Reset photo state
            setPhotoFile(null);
            setPhotoPreview(null);
        } catch (err) {
            console.error('Update error:', err);

            let errorMessage = 'Failed to update president. ';

            if (err.response) {
                errorMessage += err.response.data?.message || err.response.data?.error || `Server error: ${err.response.status}`;
            } else if (err.request) {
                errorMessage += 'No response from server. Please check your connection.';
            } else {
                errorMessage += err.message;
            }

            setError(errorMessage);
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle president deletion
    const handleDelete = async (id) => {
        if (!id) {
            setError('Invalid president ID');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this president record?')) {
            return;
        }

        setDeletingId(id);
        try {
            await presidentAPI.deletePresident(id);
            setSuccess('President deleted successfully');

            // Refresh the list
            await fetchPresidents();

            // Reset to first page if needed
            if (filteredPresidents.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            }

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Delete error:', err);

            let errorMessage = 'Failed to delete president. ';
            if (err.response) {
                errorMessage += err.response.data?.message || err.response.data?.error || `Server error: ${err.response.status}`;
            } else if (err.request) {
                errorMessage += 'No response from server.';
            } else {
                errorMessage += err.message;
            }

            setError(errorMessage);
        } finally {
            setDeletingId(null);
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    // Handle photo file change
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                setFormErrors({ ...formErrors, photo: 'Invalid file type. Please upload JPG, PNG, or GIF.' });
                return;
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                setFormErrors({ ...formErrors, photo: 'File size exceeds 5MB limit.' });
                return;
            }

            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);

            // Clear photo error if any
            if (formErrors.photo) {
                setFormErrors(prev => ({
                    ...prev,
                    photo: undefined
                }));
            }
        }
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Generate pagination items
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            items.push(
                <Pagination.Item
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
            />
        );

        return items;
    };

    return (
        <Container className="py-4">
            {/* Header Section */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="mb-1">President Management</h2>
                            <p className="text-muted mb-0">
                                Manage president records, including current and past presidents.
                            </p>
                        </div>
                        <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
                            <FaPlus className="me-2" />
                            Add President
                        </Button>
                    </div>

                    {/* Search and Filter Section */}
                    <Row className="g-3 mb-4">
                        <Col md={4}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search by name, university, email, or phone..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={selectedUniversity}
                                onChange={(e) => {
                                    setSelectedUniversity(e.target.value);
                                    setCurrentPage(1);
                                }}
                                disabled={loadingUniversities}
                            >
                                <option value="all">All Universities</option>
                                {filterUniversities.map((university, index) => (
                                    <option key={index} value={university}>
                                        {university}
                                    </option>
                                ))}
                                {filterUniversities.length === 0 && (
                                    <option value="" disabled>No universities found</option>
                                )}
                            </Form.Select>
                            {loadingUniversities && (
                                <small className="text-muted">Loading universities...</small>
                            )}
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={selectedStatus}
                                onChange={(e) => {
                                    setSelectedStatus(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active Presidents</option>
                                <option value="past">Past Presidents</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button
                                variant="outline-secondary"
                                onClick={resetFilters}
                                className="w-100"
                                title="Reset Filters"
                            >
                                <FaFilter /> Reset
                            </Button>
                        </Col>
                    </Row>

                    {/* Stats Cards */}
                    <Row className="mt-4">
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Total Presidents</h6>
                                            <h4 className="mb-0">{stats.total}</h4>
                                        </div>
                                        <div className="text-primary">
                                            <FaUser size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Active</h6>
                                            <h4 className="mb-0">{stats.active}</h4>
                                        </div>
                                        <div className="text-success">
                                            <FaCrown className="text-success" size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Past</h6>
                                            <h4 className="mb-0">{stats.past}</h4>
                                        </div>
                                        <div className="text-secondary">
                                            <FaCalendar size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Universities</h6>
                                            <h4 className="mb-0">{stats.universitiesCount}</h4>
                                        </div>
                                        <div className="text-info">
                                            <FaUniversity size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">With Photos</h6>
                                            <h4 className="mb-0">{stats.withPhotos}</h4>
                                        </div>
                                        <div className="text-warning">
                                            <FaImage size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Showing</h6>
                                            <h4 className="mb-0">{filteredPresidents.length}</h4>
                                        </div>
                                        <div className="text-muted">
                                            <FaFilter size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
                    <Alert.Heading>Error</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
                    <Alert.Heading>Success</Alert.Heading>
                    <p>{success}</p>
                </Alert>
            )}

            {/* Presidents Table */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading presidents...</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '80px' }}>Photo</th>
                                            <th>Name</th>
                                            <th>University</th>
                                            <th>Contact</th>
                                            <th>Term</th>
                                            <th>Status</th>
                                            <th style={{ width: '120px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPresidents.length > 0 ? (
                                            filteredPresidents.map(president => {
                                                const isActive = isCurrentlyActive(president.todate, president.still_working);
                                                const imagePath = president.prephotopath || president.photoPath || president.presidentphotopath || president.memberphotopath;
                                                const imageUrl = buildImageUrl(imagePath);

                                                return (
                                                    <tr key={president.id}>
                                                        <td>
                                                            <div style={{ width: '60px', height: '60px' }}>
                                                                {imageUrl ? (
                                                                    <Image
                                                                        src={imageUrl}
                                                                        roundedCircle
                                                                        fluid
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover',
                                                                            border: '2px solid #dee2e6'
                                                                        }}
                                                                        alt={president.name}
                                                                        title={imageUrl}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.style.display = 'none';
                                                                            e.target.parentElement.innerHTML = `
                                                                                <div class="bg-secondary rounded-circle d-flex align-items-center justify-content-center"
                                                                                    style="width: 100%; height: 100%;">
                                                                                    <FaUser className="text-white" />
                                                                                </div>
                                                                            `;
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center"
                                                                        style={{ width: '100%', height: '100%' }}>
                                                                        <FaUser className="text-white" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{president.name}</div>
                                                            <small className="text-muted">{president.designation || '—'}</small>
                                                            {president.message && (
                                                                <small className="text-muted d-block mt-1" style={{ fontSize: '0.8em' }}>
                                                                    {president.message.substring(0, 50)}...
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{president.university || '—'}</div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                {president.email && (
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaEnvelope className="me-2 text-muted" size={12} />
                                                                        <small className="text-truncate" style={{ maxWidth: '150px' }}>
                                                                            {president.email}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                                {president.mobile && (
                                                                    <div className="d-flex align-items-center">
                                                                        <FaPhone className="me-2 text-muted" size={12} />
                                                                        <small>{president.mobile}</small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <small className="d-block">
                                                                    <strong>From:</strong> {formatDate(president.fromdate)}
                                                                </small>
                                                                <small className="d-block">
                                                                    <strong>To:</strong> {president.still_working === '1' || president.still_working === 1 ? 'Present' : formatDate(president.todate)}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg={isActive ? 'success' : 'secondary'}
                                                                className="text-uppercase"
                                                            >
                                                                {isActive ? 'Active' : 'Past'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    onClick={() => handleEdit(president)}
                                                                    size="sm"
                                                                    disabled={deletingId !== null}
                                                                    title="Edit"
                                                                >
                                                                    <FaEdit />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    onClick={() => handleDelete(president.id)}
                                                                    size="sm"
                                                                    disabled={deletingId !== null}
                                                                    title="Delete"
                                                                >
                                                                    {deletingId === president.id ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : (
                                                                        <FaTrash />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="py-4">
                                                        <FaSearch size={48} className="text-muted mb-3" />
                                                        <h5>No presidents found</h5>
                                                        <p className="text-muted">
                                                            {searchTerm || selectedUniversity !== 'all' || selectedStatus !== 'all'
                                                                ? 'Try changing your search or filters'
                                                                : 'Add your first president to get started'}
                                                        </p>
                                                        {searchTerm || selectedUniversity !== 'all' || selectedStatus !== 'all' ? (
                                                            <Button variant="outline-primary" onClick={resetFilters}>
                                                                Reset Filters
                                                            </Button>
                                                        ) : (
                                                            <Button variant="primary" onClick={handleCreate}>
                                                                <FaPlus className="me-2" /> Add President
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {filteredPresidents.length > 0 && totalPages > 1 && (
                                <div className="p-3 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted">
                                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} presidents
                                        </div>
                                        <Pagination className="mb-0">
                                            {renderPagination()}
                                        </Pagination>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Edit President Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit President</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdate}>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Full Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        isInvalid={!!formErrors.name}
                                        disabled={isUpdating}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>University *</Form.Label>
                                    <Form.Select
                                        name="university"
                                        value={formData.university}
                                        onChange={handleChange}
                                        required
                                        isInvalid={!!formErrors.university}
                                        disabled={isUpdating || loadingUniversities}
                                    >
                                        <option value="">Select University</option>
                                        {universities.map((university, index) => (
                                            <option key={index} value={university}>
                                                {university}
                                            </option>
                                        ))}
                                        {universities.length === 0 && loadingUniversities && (
                                            <option value="" disabled>Loading universities...</option>
                                        )}
                                        {universities.length === 0 && !loadingUniversities && (
                                            <option value="" disabled>No universities available</option>
                                        )}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.university}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Designation</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="designation"
                                        value={formData.designation}
                                        onChange={handleChange}
                                        disabled={isUpdating}
                                        placeholder="e.g., President, Vice President, etc."
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                disabled={isUpdating}
                                                placeholder="email@example.com"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Mobile</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                disabled={isUpdating}
                                                placeholder="+91 1234567890"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Message/Bio</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        disabled={isUpdating}
                                        placeholder="Enter a message or bio for the president..."
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Start Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="fromdate"
                                                value={formData.fromdate}
                                                onChange={handleChange}
                                                required
                                                isInvalid={!!formErrors.fromdate}
                                                disabled={isUpdating}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.fromdate}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Status</Form.Label>
                                            <Form.Select
                                                name="still_working"
                                                value={formData.still_working}
                                                onChange={handleChange}
                                                disabled={isUpdating}
                                            >
                                                <option value="0">Past President</option>
                                                <option value="1">Current President</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {formData.still_working === '0' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label style={formLabelStyle}>End Date *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="todate"
                                            value={formData.todate}
                                            onChange={handleChange}
                                            isInvalid={!!formErrors.todate}
                                            disabled={isUpdating}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.todate}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Photo</Form.Label>
                                    <div className="mb-3 text-center">
                                        {photoPreview ? (
                                            <Image
                                                src={photoPreview}
                                                roundedCircle
                                                fluid
                                                style={{ width: '150px', height: '150px', objectFit: 'cover', border: '3px solid #dee2e6' }}
                                                alt="Preview"
                                                className="mx-auto d-block"
                                            />
                                        ) : (
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                                style={{ width: '150px', height: '150px' }}>
                                                <FaImage size={48} className="text-muted" />
                                            </div>
                                        )}
                                    </div>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        disabled={isUpdating}
                                    />
                                    <Form.Text className="text-muted">
                                        Upload a new photo (JPG, PNG). Leave empty to keep current photo.
                                    </Form.Text>
                                </Form.Group>
                                {formErrors.photo && (
                                    <div className="text-danger small mt-1">{formErrors.photo}</div>
                                )}
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-3">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="me-2" disabled={isUpdating}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isUpdating}>
                                {isUpdating ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    'Update President'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Create President Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Create New President</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateSubmit}>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Full Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        isInvalid={!!formErrors.name}
                                        disabled={isCreating}
                                        placeholder="Enter full name"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>University *</Form.Label>
                                    <Form.Select
                                        name="university"
                                        value={formData.university}
                                        onChange={handleChange}
                                        required
                                        isInvalid={!!formErrors.university}
                                        disabled={isCreating || loadingUniversities}
                                    >
                                        <option value="">Select University</option>
                                        {universities.map((university, index) => (
                                            <option key={index} value={university}>
                                                {university}
                                            </option>
                                        ))}
                                        {universities.length === 0 && loadingUniversities && (
                                            <option value="" disabled>Loading universities...</option>
                                        )}
                                        {universities.length === 0 && !loadingUniversities && (
                                            <option value="" disabled>No universities available</option>
                                        )}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.university}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Designation</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="designation"
                                        value={formData.designation}
                                        onChange={handleChange}
                                        disabled={isCreating}
                                        placeholder="e.g., President, Vice President, etc."
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                disabled={isCreating}
                                                placeholder="email@example.com"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Mobile</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                disabled={isCreating}
                                                placeholder="+91 1234567890"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Message/Bio</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        disabled={isCreating}
                                        placeholder="Enter a message or bio for the president..."
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Start Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="fromdate"
                                                value={formData.fromdate}
                                                onChange={handleChange}
                                                required
                                                isInvalid={!!formErrors.fromdate}
                                                disabled={isCreating}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.fromdate}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={formLabelStyle}>Status</Form.Label>
                                            <Form.Select
                                                name="still_working"
                                                value={formData.still_working}
                                                onChange={handleChange}
                                                disabled={isCreating}
                                            >
                                                <option value="0">Past President</option>
                                                <option value="1">Current President</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {formData.still_working === '0' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label style={formLabelStyle}>End Date *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="todate"
                                            value={formData.todate}
                                            onChange={handleChange}
                                            isInvalid={!!formErrors.todate}
                                            disabled={isCreating}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.todate}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={formLabelStyle}>Photo *</Form.Label>
                                    <div className="mb-3 text-center">
                                        {photoPreview ? (
                                            <Image
                                                src={photoPreview}
                                                roundedCircle
                                                fluid
                                                style={{ width: '150px', height: '150px', objectFit: 'cover', border: '3px solid #dee2e6' }}
                                                alt="Preview"
                                                className="mx-auto d-block"
                                            />
                                        ) : (
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                                style={{ width: '150px', height: '150px' }}>
                                                <FaImage size={48} className="text-muted" />
                                            </div>
                                        )}
                                    </div>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        required={!photoPreview}
                                        disabled={isCreating}
                                        isInvalid={!!formErrors.photo}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.photo}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-muted">
                                        Upload a photo (JPG, PNG). Required for new president.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-3">
                            <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="me-2" disabled={isCreating}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isCreating}>
                                {isCreating ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Creating...
                                    </>
                                ) : (
                                    'Create President'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default PresidentList;